import { createBrowserClient } from '@supabase/ssr';
import type { Database } from '@/types/database';

/**
 * Browser-side Supabase client — singleton per-tab.
 *
 * The singleton pattern avoids the "duplicate GoTrue instance / 429 storm"
 * bug from earlier. But it comes with a subtle risk: once a user signs
 * out, the same cached client gets reused by the next user on the same
 * tab. If any in-flight promise or long-lived subscription still holds a
 * reference to the client's old session, that state bleeds across logins.
 *
 * Mitigations in this file:
 *   1. A one-time `onAuthStateChange` listener wipes the module-scoped
 *      cache pointer on SIGNED_OUT. The next `createClient()` call builds
 *      a fresh client. Existing holders of the OLD reference see an
 *      unauthenticated client (which is fine — their queries will 401
 *      and surface the session loss naturally).
 *   2. Hard-navigation on sign-out (Sidebar.handleSignOut) is still
 *      preferred — flushes React tree + timers + websockets in one shot.
 */

let browserClient: ReturnType<typeof createBrowserClient<Database>> | null = null;
let signOutHandlerBound = false;

function buildClient(): ReturnType<typeof createBrowserClient<Database>> {
  const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL;
  const supabaseAnonKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;

  if (!supabaseUrl || !supabaseAnonKey) {
    throw new Error(
      'Missing Supabase environment variables. ' +
        'NEXT_PUBLIC_SUPABASE_URL and NEXT_PUBLIC_SUPABASE_ANON_KEY must be set.'
    );
  }

  const client = createBrowserClient<Database>(supabaseUrl, supabaseAnonKey, {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: false,
    },
  });

  // Bind exactly once to the FIRST client. Subsequent rebuilds (after sign-
  // out) inherit fresh Supabase auth state; we don't need multiple listeners.
  if (!signOutHandlerBound) {
    client.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_OUT') {
        // Next createClient() call will construct a fresh client with no
        // residual session cache. Anyone holding the old reference will
        // see an unauth'd state, which is the correct post-signout view.
        browserClient = null;
      }
    });
    signOutHandlerBound = true;
  }

  return client;
}

export function createClient(): ReturnType<typeof createBrowserClient<Database>> {
  if (browserClient) return browserClient;
  browserClient = buildClient();
  return browserClient;
}
